import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Stack, Container, Typography, Button, TextField, Box, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { PATHS } from 'src/constants/config';
import { authApi } from 'src/api/authApi';
import { setJWT } from 'src/store/slices/jwtTokenSlice';
import { resetQueue } from 'src/store/slices/queueSlice';

// Temporary function to determine user type - to be replaced with actual API call
const checkUserType = (email) => {
  // Hardcoded check for demo purposes
  if (
    email.endsWith('@seoplus.com') ||
    email.endsWith('@seoplus.ca') ||
    email.endsWith('@abror.io') ||
    email.endsWith('abrkamalov@gmail.com') ||
    email.endsWith('legocad@gmail.com')
  ) {
    return 'staff';
  }
  return 'expert';
};

const emailValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const fullValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(8, 'Password is too short - should be 8 chars minimum.').required('Password is required'),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    dispatch(resetQueue());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: userType === 'staff' ? fullValidationSchema : emailValidationSchema,
    onSubmit: async (values, { setErrors }) => {
      if (!userType) {
        // First step - email submission
        const type = checkUserType(values.email);

        // TEMPORARILY COMMENTED OUT: Expert login disabled
        // if (type === 'expert') {
        //   try {
        //     const response = await authApi.expertSendToken(values.email);
        //     if (!response.data) {
        //       toast.error('Login failed');
        //       setErrors({ email: 'Failed to send login link' });
        //     } else if (response.data.error) {
        //       toast.error(response.data.error);
        //       setErrors({ email: 'Failed to send login link' });
        //     } else {
        //       toast.success('Login link has been sent to your email');
        //       setUserType('expert');
        //     }
        //   } catch (error) {
        //     toast.error(error.message);
        //     setErrors({ email: 'Failed to send login link' });
        //   }
        // } else {
        //   // For staff, just show password field
        //   setUserType(type);
        // }

        if (type === 'expert') {
          setErrors({ email: 'Invalid email' });
          return;
        } else {
          // For staff, just show password field
          setUserType(type);
        }
        return;
      }

      // Handle staff login with password
      try {
        const response = await authApi.login(values.email, values.password);

        if (response) {
          dispatch(setJWT({ token: response }));
          localStorage.setItem('jwtToken', response);
          toast.success('Login successful');
          navigate(PATHS.PITCH_INBOX.value);
        } else {
          setErrors({ email: ' ', password: 'Invalid email or password' });
        }
      } catch (error) {
        setErrors({ email: ' ', password: 'Invalid email or password' });
      }
    },
  });

  const handleBack = () => {
    setUserType(null);
    formik.setFieldValue('password', '');
  };

  const renderLoginForm = () => (
    <Container maxWidth="sm">
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Log in
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.email}
            disabled={userType !== null}
          />

          {userType === 'staff' && (
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.password}
            />
          )}

          {/* TEMPORARILY COMMENTED OUT: Expert login UI disabled */}
          {/* {userType === 'expert' && (
            <Alert severity="info" sx={{ border: '1px solid #00c853', bgcolor: '#e8f5e9' }}>
              Check your inbox and click the magic link to log in. If you don&apos;t see it, contact your inviter.
            </Alert>
          )} */}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {(!userType || userType === 'staff') && (
            <Button fullWidth size="large" type="submit" variant="contained">
              {!userType ? 'Continue' : 'Log In'}
            </Button>
          )}

          {userType && (
            <Button fullWidth size="large" variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Back
            </Button>
          )}
        </Stack>
      </form>
    </Container>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            flex: '1 1 70%',
            backgroundImage: 'url(/assets/seo-auth-2.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <Box
        sx={{
          flex: isMobile ? '1' : '1 1 30%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          bgcolor: 'white',
        }}
      >
        {renderLoginForm()}
      </Box>
    </Box>
  );
};

export default Login;
